import { Link, useRouterState } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import CloudinaryImage from "@/components/cloudinary-image";
import SignupModal from "@/components/signup-modal";
import useAuth from "@/hooks/use-auth";
import useCurrentLocation from "@/hooks/use-current-location";
import { cn } from "@/lib/utils";

import { ConnectIcon, ExploreIcon, ProfileIcon } from "./icons";
import MenuTabItem from "./menu-tab-item";

const TabsEnum = {
  CONNECT: "CONNECT",
  EXPLORE: "EXPLORE",
  PROFILE: "PROFILE",
} as const;

export default function MenuTabs({
  className,
  ...props
}: React.ComponentProps<"ul">) {
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);

  const { toExploreUrl } = useCurrentLocation();
  const { me: user } = useAuth();
  const asPath = useRouterState({ select: (s) => s.location.href });

  const activeTab = useMemo(() => {
    if (asPath.startsWith("/explore")) {
      return TabsEnum.EXPLORE;
    }
    if (asPath === "/connect") {
      return TabsEnum.CONNECT;
    }
    if (!user) {
      return null;
    }
    if (asPath.startsWith(`/@${user.username}`)) {
      return TabsEnum.PROFILE;
    }

    return null;
  }, [asPath, user]);

  const userImage = useMemo(() => {
    if (!user?.isPublic) {
      return <ProfileIcon className="size-5" />;
    }

    return (
      <CloudinaryImage
        className={cn(
          "size-5 rounded-full border-rose-red",
          activeTab === TabsEnum.PROFILE && "border-1"
        )}
        rW={60}
        size={20}
        user={user}
      />
    );
  }, [user, activeTab]);

  return (
    <>
      <ul
        className={cn("flex w-full justify-between md:w-auto", className)}
        {...props}
      >
        <MenuTabItem
          asChild
          icon={<ExploreIcon className="size-6 text-inherit" />}
          isSelected={activeTab === TabsEnum.EXPLORE}
        >
          <Link to={toExploreUrl()}>Explore</Link>
        </MenuTabItem>
        <MenuTabItem
          asChild={!!user}
          icon={<ConnectIcon className="size-6 text-inherit" />}
          isSelected={activeTab === TabsEnum.CONNECT}
          onClick={user ? undefined : () => setIsSignupModalOpen(true)}
        >
          {user ? <Link to="/connect">Connect</Link> : "Connect"}
        </MenuTabItem>
        {user ? (
          <MenuTabItem
            asChild
            icon={userImage}
            isSelected={activeTab === TabsEnum.PROFILE}
          >
            <Link to={`/@${user.username}`}>Profile</Link>
          </MenuTabItem>
        ) : (
          <MenuTabItem
            icon={<ProfileIcon className="size-6" />}
            onClick={() => setIsSignupModalOpen(true)}
          >
            Profile
          </MenuTabItem>
        )}
      </ul>
      <SignupModal
        onOpenChange={setIsSignupModalOpen}
        open={isSignupModalOpen}
      />
    </>
  );
}
